import { useState, useEffect, useCallback, useRef } from "react";

export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function useThrottle(minInterval: number = 500) {
  const lastCallTime = useRef<number>(0);
  const [isThrottled, setIsThrottled] = useState(false);

  const checkThrottle = useCallback((): boolean => {
    const now = Date.now();
    const timeSinceLastCall = now - lastCallTime.current;
    
    if (timeSinceLastCall < minInterval) {
      setIsThrottled(true);
      setTimeout(() => setIsThrottled(false), minInterval - timeSinceLastCall);
      return false; // Throttled - don't allow
    }
    
    lastCallTime.current = now;
    return true; // Allowed
  }, [minInterval]);

  const reset = useCallback(() => {
    lastCallTime.current = 0;
    setIsThrottled(false);
  }, []);

  return { checkThrottle, isThrottled, reset };
}
